import {
  FormGroup,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  InputGroup,
  InputGroupText,
  Tooltip,
} from "reactstrap";
import { Copy, QrCode, Download, Check } from "lucide-react";
import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import brand_logo from "../brand-logo.png";

const formatSlug = (value = "") =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\-_.~]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const BASE_URL = `${process.env.REACT_APP_BASE_URL_FORM}`;

const PreviewFeePaymentURL = ({
  value,
  onChange,
  onBlur,
  error,
  required = false,
  slugStatus,
}) => {
  const [qrOpen, setQrOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [logoBase64, setLogoBase64] = useState("");
  const [copyTipOpen, setCopyTipOpen] = useState(false);
  const [qrTipOpen, setQrTipOpen] = useState(false);

  const slug = value || "";
  const fullUrl = slug ? `${BASE_URL}${slug}` : "";

  const handleCopy = async () => {
    if (!slug) return;
    await navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const imageToBase64 = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  useEffect(() => {
    imageToBase64(brand_logo).then(setLogoBase64);
  }, []);

  const downloadPNG = () => {
    const svg = document.querySelector(".qr-svg svg");
    if (!svg) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);
    const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 600;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, size, size);
      const png = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = png;
      link.download = `payment-qr-${slug}.png`;
      link.click();
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const BRAND_QR_COLOR = "#0C1B39";

  // Disable copy/QR if slug is empty or slugStatus indicates it's already in use
  const isUnavailable =
    !slug || (slugStatus && slugStatus.includes("already in use"));

  return (
    <>
      <FormGroup>
        <Label className="fw-semibold">
          Preview Payment URL {required && <span className="text-danger">*</span>}
        </Label>

        <InputGroup>
          <InputGroupText id="paymentSlugInput" className="text-muted">
            {BASE_URL}
          </InputGroupText>

          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={(e) => {
              onChange(formatSlug(e.target.value));
              onBlur?.();
            }}
            placeholder="donation-form"
            autoComplete="off"
          />

          {/* Copy button */}
          <InputGroupText
            id="copyUrlBtn"
            role="button"
            onClick={!isUnavailable ? handleCopy : undefined}
            className={`cursor-pointer ${isUnavailable ? "disabled" : ""}`}
            title="Copy URL"
            i
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </InputGroupText>

          <Tooltip
            target="copyUrlBtn"
            isOpen={copyTipOpen}
            toggle={() => setCopyTipOpen(!copyTipOpen)}
            placement="top"
          >
            Copy full payment URL
          </Tooltip>

          {/* QR button */}
          <InputGroupText
            id="qrBtn"
            role="button"
            onClick={!isUnavailable ? () => setQrOpen(true) : undefined}
            className={`cursor-pointer ${isUnavailable ? "disabled" : ""}`}
            title="Show QR"
          >
            <QrCode size={16} />
          </InputGroupText>

          <Tooltip
            target="qrBtn"
            isOpen={qrTipOpen}
            toggle={() => setQrTipOpen(!qrTipOpen)}
            placement="top"
          >
            Generate QR code
          </Tooltip>

          {error && <div className="invalid-feedback d-block">{error}</div>}
        </InputGroup>

        {/* Slug availability message */}
        {slugStatus && (
          <div
            className={slugStatus.includes("available") ? "text-success" : "text-danger"}
            style={{ fontSize: "0.875rem", marginTop: "4px" }}
          >
            {slugStatus}
          </div>
        )}
      </FormGroup>

      {/* QR Modal */}
      <Modal
        isOpen={qrOpen}
        toggle={() => setQrOpen(false)}
        centered
        size="md"
        className="premium-qr-modal"
      >
        <ModalHeader toggle={() => setQrOpen(false)}>
          <div>
            <h5 className="modal-title mb-0">Scan Payment URL</h5>
            <small className="text-muted">
              Scan using mobile camera or payment app
            </small>
          </div>
        </ModalHeader>

        <ModalBody>
          <div className="qr-card">
            <div className="qr-svg">
              {logoBase64 && !isUnavailable && (
                <QRCodeSVG
                  value={fullUrl}
                  size={220}
                  level="H"
                  includeMargin
                  fgColor={BRAND_QR_COLOR}
                  bgColor="#ffffff"
                  imageSettings={{
                    src: logoBase64,
                    width: 36,
                    height: 36,
                    excavate: true,
                  }}
                />
              )}
            </div>
            <div className="qr-hint">Scan to open secure payment link</div>
          </div>

          <div className="qr-url-inline url-actions">
            <span className="qr-url-text">{fullUrl}</span>
            <button type="button" onClick={handleCopy} disabled={isUnavailable}>
              <Copy size={16} />
              {copied ? "Copied" : "Copy"}
            </button>
          </div>

          <div className="qr-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={downloadPNG}
              disabled={isUnavailable}
            >
              <Download size={16} className="me-2" />
              Download QR
            </button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default PreviewFeePaymentURL;
